import { DateSelectArg, EventDropArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { useCallback, useEffect, useRef, useState } from 'react';
// hooks
import { useResponsive } from './use-responsive';
// utils
import { fTimestamp } from '../utils/format-time';
// types
import {
  ICalendarEvent,
  ICalendarRange,
  ICalendarView,
} from '../types/calendar';

// ----------------------------------------------------------------------

export default function useCalendar() {
  const calendarRef = useRef<FullCalendar>(null);
  const [calendarEl, setCalendarEl] = useState<FullCalendar | null>(null);

  useEffect(() => {
    setCalendarEl(calendarRef.current);
  }, [calendarRef]);

  const mdUp = useResponsive('up', 'md');

  const [date, setDate] = useState(new Date());

  const [openForm, setOpenForm] = useState(false);

  const [selectedRange, setSelectedRange] = useState<ICalendarRange>(null);

  const [view, setView] = useState<ICalendarView>(
    mdUp ? 'timeGridWeek' : 'timeGridDay',
  );

  const onOpenForm = useCallback(() => {
    setOpenForm(true);
  }, []);

  const onInitialView = useCallback(() => {
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      const newView = mdUp ? 'timeGridWeek' : 'timeGridDay';
      calendarApi.changeView(newView);
      setView(newView);
    }
  }, [calendarEl, mdUp]);

  const onChangeView = useCallback(
    (newView: ICalendarView) => {
      if (calendarEl) {
        const calendarApi = calendarEl.getApi();

        calendarApi.changeView(newView);
        setView(newView);
      }
    },
    [calendarEl],
  );

  const onDateToday = useCallback(() => {
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  }, [calendarEl]);

  const onDatePrev = useCallback(() => {
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  }, [calendarEl]);

  const onDateNext = useCallback(() => {
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();

      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  }, [calendarEl]);

  const onChangeDate = useCallback(
    (newDate: Date) => {
      if (calendarEl) {
        const calendarApi = calendarEl.getApi();

        calendarApi.gotoDate(newDate);
        setDate(newDate);
      }
    },
    [calendarEl],
  );

  const onSelectRange = useCallback(
    (arg: DateSelectArg) => {
      if (calendarEl) {
        const calendarApi = calendarEl.getApi();

        calendarApi.unselect();
      }
      onOpenForm();
      setSelectedRange({
        start: fTimestamp(arg.start),
        end: fTimestamp(arg.end),
      });
    },
    [calendarEl, onOpenForm],
  );

  // const onClickEvent = useCallback((arg: EventClickArg) => {
  //   const { event } = arg;

  // }, []);

  const onResizeEvent = useCallback(
    (
      arg: EventResizeDoneArg,
      updateEvent: (eventData: Partial<ICalendarEvent>) => void,
    ) => {
      const { event } = arg;

      updateEvent({
        id: event.id,
        allDay: event.allDay,
        start: fTimestamp(event.start),
        end: fTimestamp(event.end),
      });
    },
    [],
  );

  const onDropEvent = useCallback(
    (
      arg: EventDropArg,
      updateEvent: (eventData: Partial<ICalendarEvent>) => void,
    ) => {
      const { event } = arg;

      updateEvent({
        id: event.id,
        allDay: event.allDay,
        start: fTimestamp(event.start),
        end: fTimestamp(event.end),
      });
    },
    [],
  );

  const onClickEventInFilters = useCallback(
    (eventId: string) => {
      if (eventId) {
        onOpenForm();
      }
    },
    [onOpenForm],
  );

  return {
    calendarRef,
    //
    view,
    date,
    //
    onDatePrev,
    onDateNext,
    onDateToday,
    onChangeDate,
    onDropEvent,
    onChangeView,
    onSelectRange,
    onResizeEvent,
    onInitialView,
    //
    openForm,
    onOpenForm, //
    selectedRange,
    //
    onClickEventInFilters,
  };
}
